import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import RadioIcon from "@mui/icons-material/SettingsInputAntenna";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { onboardingService } from "src/services";

/* ---------- Light theme tokens ---------- */
const BG = "#ffffff";
const CARD_BG = "#ffffff";
const CARD_BG_SOFT = "#f6f8f7";
const BORDER = "rgba(0, 0, 0, 0.75)";
const TEXT = "#111815";
const MUTED = "#5b6b62";
const GREEN = "blue";
const GREEN_STRONG = "blue";
const GREEN_TEXT = "#062617";
const LINK = "#63b7ff";

const CONTAINER_WIDTH = 760;

const CAPABILITIES = [
  {
    icon: RadioIcon,
    title: "Live charger operations",
    text: "Monitor status, connectors, availability and remote commands.",
  },
  {
    icon: ReceiptLongOutlinedIcon,
    title: "Tariffs & price groups",
    text: "Create energy, time and session-based charging prices.",
  },
  {
    icon: GroupOutlinedIcon,
    title: "Teams & access",
    text: "Organise operations and give each member the right access.",
  },
  {
    icon: ShowChartIcon,
    title: "Transactions & reports",
    text: "Track energy, revenue, sessions and operational performance.",
  },
  {
    icon: CurrencyRupeeIcon,
    title: "Payments & settlements",
    text: "Connect payment gateways and follow collections and payouts.",
  },
  {
    icon: SellOutlinedIcon,
    title: "Drivers, RFID & wallets",
    text: "Manage charging access, tags, customers and account balances.",
  },
];

function formatMoney(amount) {
  return `₹${Number(amount).toLocaleString("en-IN")}`;
}

function QuantityRow({ label, rate, count, onChange }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={1}
      sx={{ py: 0.75 }}
    >
      <Box>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: TEXT }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.15 }}>
          {formatMoney(rate)} / charger / year
        </Typography>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: "9px",
          overflow: "hidden",
          bgcolor: "transparent",
        }}
      >
        <IconButton
          size="small"
          onClick={() => onChange(Math.max(0, count - 1))}
          sx={{ borderRadius: 0, color: TEXT, px: 1 }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Typography
          sx={{
            px: 1.25,
            fontSize: 13.5,
            fontWeight: 700,
            minWidth: 20,
            textAlign: "center",
            color: TEXT,
          }}
        >
          {count}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onChange(count + 1)}
          sx={{ borderRadius: 0, color: TEXT, px: 1 }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  );
}

export default function PlanSelectionStep({ onNext, onBack, country }) {
  const [plan, setPlan] = useState(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const [acCount, setAcCount] = useState(0);
  const [dcCount, setDcCount] = useState(0);

  useEffect(() => {
    let isCancelled = false;

    const fetchPricingPlan = async () => {
      setIsLoadingPlans(true);
      setPlansError(null);
      try {
        const response = await onboardingService.getPricingPlan(country);
        const list = response?.data?.data ?? [];

        if (!isCancelled) {
          if (response?.data?.success && Array.isArray(list) && list.length) {
            const first = list[0];
            setPlan({
              id: first.planId,
              tagline: first.tagline,
              audience: first.targetSegment,
              acRate: Number(first.acChargerRate),
              dcRate: Number(first.dcChargerRate),
              noTxnRate: Number(first.noTransactionPlan),
              currency: first.currency,
            });
          } else {
            setPlan(null);
            setPlansError("No pricing plans available for this country.");
          }
        }
      } catch (err) {
        if (!isCancelled) {
          setPlan(null);
          setPlansError(
            err?.response?.data?.message ||
              "Couldn't load pricing plans. Please try again.",
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingPlans(false);
        }
      }
    };

    fetchPricingPlan();

    return () => {
      isCancelled = true;
    };
  }, [country]);

  const totalCost = useMemo(() => {
    if (!plan) return 0;
    return acCount * plan.acRate + dcCount * plan.dcRate;
  }, [plan, acCount, dcCount]);

  const hasSelection = acCount > 0 || dcCount > 0;

  const handleNext = () => {
    if (!plan || !hasSelection) return;

    const line_items = [];
    if (acCount > 0) {
      line_items.push({
        id: `${plan.id}-ac`,
        label: "AC Charger",
        plan: plan.id,
        quantity: acCount,
        unit_price: plan.acRate,
        line_total: acCount * plan.acRate,
      });
    }
    if (dcCount > 0) {
      line_items.push({
        id: `${plan.id}-dc`,
        label: "DC Charger",
        plan: plan.id,
        quantity: dcCount,
        unit_price: plan.dcRate,
        line_total: dcCount * plan.dcRate,
      });
    }

    onNext({
      line_items,
      total_cost: totalCost,
      currency: plan.currency ?? "INR",
      duration: "Y",
    });
  };

  if (isLoadingPlans) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8, bgcolor: BG }}>
        <CircularProgress sx={{ color: GREEN }} />
      </Box>
    );
  }

  if (plansError) {
    return (
      <Box sx={{ p: 2, bgcolor: BG }}>
        <Alert severity="error">{plansError}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: BG,
        borderRadius: "18px",
        border: `1px solid ${BORDER}`,
        p: { xs: 2, md: 3 },
        maxWidth: CONTAINER_WIDTH,
        mx: "auto",
      }}
    >
      {/* Hero: left intro + right pricing card */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2, md: 3 }}
        alignItems="stretch"
      >
        {/* Left intro */}
        <Box
          sx={{
            flex: 1.15,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box>
            <Typography
              sx={{
                color: GREEN,
                fontSize: 11.5,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                mb: 0.75,
              }}
            >
              One simple annual subscription
            </Typography>
            <Typography
              sx={{
                color: TEXT,
                fontWeight: 800,
                fontSize: { xs: 24, md: 30 },
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                mb: 1.25,
              }}
            >
              Everything you need to operate your EV charging network.
            </Typography>
            <Typography
              sx={{
                color: MUTED,
                fontSize: 13.5,
                lineHeight: 1.5,
                mb: 1.75,
                maxWidth: 440,
              }}
            >
              Start with the chargers you have today. Add more whenever your
              network grows—without changing plans.
            </Typography>

            <Stack spacing={1}>
              <Stack direction="row" spacing={2.5}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <ShieldOutlinedIcon sx={{ color: MUTED, fontSize: 16 }} />
                  <Typography sx={{ color: MUTED, fontSize: 12.5 }}>
                    Secure cloud CPMS
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <HeadsetMicOutlinedIcon sx={{ color: MUTED, fontSize: 16 }} />
                  <Typography sx={{ color: MUTED, fontSize: 12.5 }}>
                    Zynka support
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <AutorenewIcon sx={{ color: MUTED, fontSize: 16 }} />
                <Typography sx={{ color: MUTED, fontSize: 12.5 }}>
                  Annual billing
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Spacer pushes Back down so it fills the remaining height,
              landing roughly level with the bottom of the pricing card */}
          <Box sx={{ flexGrow: 1, minHeight: 20 }} />

          <Button
            onClick={onBack}
            sx={{
              alignSelf: "flex-start",
             
              color: "black",
              border: "1px solid rgba(10, 10, 10, 0.63)",
              borderRadius: "10px",
              px: 2.5,
              py: 0.75,
              fontWeight: 800,
              fontSize: 14,
              textTransform: "none",
              "&:hover": { bgcolor: "rgba(122,217,168,0.22)" },
            }}
          >
            Back
          </Button>
        </Box>

        {/* Right pricing card */}
        {plan && (
          <Box
            sx={{
              flex: 1,
              maxWidth: { md: 380 },
              bgcolor: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: "14px",
              p: { xs: 2, md: 2.25 },
              alignSelf: "flex-start",
            }}
          >
            <Typography
              sx={{
                color: GREEN,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                mb: 0.5,
              }}
            >
              Build your subscription
            </Typography>
            <Typography
              sx={{ color: TEXT, fontWeight: 800, fontSize: 19, mb: 0.25 }}
            >
              Choose charger count
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: 12.5, mb: 1 }}>
              Your CPMS capabilities are included.
            </Typography>

            <Box>
              <QuantityRow
                label="AC chargers"
                rate={plan.acRate}
                count={acCount}
                onChange={setAcCount}
              />
              <Box sx={{ borderTop: `1px solid ${BORDER}` }} />
              <QuantityRow
                label="DC chargers"
                rate={plan.dcRate}
                count={dcCount}
                onChange={setDcCount}
              />
            </Box>

            <Box sx={{ borderTop: `1px solid ${BORDER}`, mt: 0.5, pt: 1.25 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 0.5 }}
              >
                <Typography sx={{ color: TEXT, fontSize: 13.5 }}>
                  Annual subscription
                </Typography>
                <Typography
                  sx={{ color: TEXT, fontSize: 13.5, fontWeight: 700 }}
                >
                  {formatMoney(totalCost)}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 0.5 }}
              >
                <Typography sx={{ color: MUTED, fontSize: 12.5 }}>
                  Taxes
                </Typography>
                <Typography sx={{ color: MUTED, fontSize: 12.5 }}>
                  Calculated at checkout
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  sx={{ color: TEXT, fontWeight: 800, fontSize: 16.5 }}
                >
                  Total
                </Typography>
                <Typography sx={{ color: TEXT, fontWeight: 800, fontSize: 19 }}>
                  {formatMoney(totalCost)}
                </Typography>
              </Stack>
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={handleNext}
              disabled={!hasSelection}
              sx={{
                mt: 1.5,
                "&:hover": {
                  bgcolor: "primary.main",
                },
                "&.Mui-disabled": {
                  color: "grey",
                },
              }}
            >
              Continue to signup
            </Button>

            {!hasSelection && (
              <Typography
                sx={{
                  fontSize: 11,
                  color: MUTED,
                  mt: 0.75,
                  textAlign: "center",
                }}
              >
                Select at least one AC or DC charger to continue.
              </Typography>
            )}
          </Box>
        )}
      </Stack>

      {/* Capabilities */}
      <Box sx={{ mt: 3 }}>
        <Typography
          sx={{
            color: GREEN,
            fontSize: 11.5,
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            mb: 0.5,
          }}
        >
          Included with every charger
        </Typography>
        <Typography
          sx={{
            color: TEXT,
            fontWeight: 800,
            fontSize: { xs: 18, md: 20 },
            mb: 1.5,
          }}
        >
          Full CPMS capability, from day one
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 1.25,
          }}
        >
          {CAPABILITIES.map((cap) => {
            const Icon = cap.icon;
            return (
              <Box
                key={cap.title}
                sx={{
                  bgcolor: CARD_BG,
                  border: `1px solid ${BORDER}`,
                  borderRadius: "12px",
                  p: 1.5,
                }}
              >
                <Icon sx={{ color: MUTED, fontSize: 18, mb: 0.75 }} />
                <Typography
                  sx={{ color: TEXT, fontWeight: 700, fontSize: 14, mb: 0.4 }}
                >
                  {cap.title}
                </Typography>
                <Typography
                  sx={{ color: MUTED, fontSize: 12.5, lineHeight: 1.45 }}
                >
                  {cap.text}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Bottom commercial cards */}
      <Box
        sx={{
          mt: 1.5,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            bgcolor: CARD_BG_SOFT,
            border: `1px solid ${BORDER}`,
            borderRadius: "12px",
            p: 1.75,
          }}
        >
          <Typography
            sx={{
              color: GREEN,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            Transaction fee
          </Typography>
          <Typography
            sx={{ color: TEXT, fontWeight: 800, fontSize: 19, mb: 0.5 }}
          >
            ₹1.25 / kWh
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 12.5 }}>
            Applied to energy delivered through paid charging sessions.
          </Typography>
        </Box>

        <Box
          sx={{
            bgcolor: CARD_BG_SOFT,
            border: `1px solid ${BORDER}`,
            borderRadius: "12px",
            p: 1.75,
          }}
        >
          <Typography
            sx={{
              color: GREEN,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            White-label CPMS
          </Typography>
          <Typography
            sx={{ color: TEXT, fontWeight: 800, fontSize: 19, mb: 0.5 }}
          >
            Custom pricing
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 12.5 }}>
            Contact{" "}
            <Box component="span" sx={{ color: LINK, fontWeight: 600 }}>
              zynkasupport@zynka.ai
            </Box>{" "}
            or{" "}
            <Box component="span" sx={{ color: LINK, fontWeight: 600 }}>
              +91 96302 01482
            </Box>
            .
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
